import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import * as _ from "lodash";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { PictureUploadState } from "src/app/annotation/canvas/canvas.component";
import { SavingStateService } from "src/app/core/survey/shared/services/saving-state.service";
import { InsideSurvey, Layout } from "src/app/shared/Models/read";
import { LayoutUnitTypeEnum } from "src/app/shared/Models/read/LayoutUnitType";
import { LayoutDto } from "src/app/shared/Models/write/LayoutDto";
import { DataConvertingService } from "src/app/shared/services/data-converting.service";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { ToastService } from "src/app/shared/toast.service";
import { BuildingLayoutService } from "../../../services/buildingLayout.service";
import { InsideSurveyBuildingLayoutCanvasComponent } from "./editor-canvas/layout-editor-canvas.component";
import { UnsavedDialogComponent } from "./unsaved-dialog/unsaved-dialog.component";
@Component({
  selector: "app-inside-survey-building-layout-editor",
  templateUrl: "./inside-survey-building-layout-editor.component.html",
  styleUrls: ["./inside-survey-building-layout-editor.component.scss"],
})
export class InsideSurveyBuildingLayoutComponent implements OnInit, OnDestroy {
  @Input()
  survey: InsideSurvey;
  @Output() imageSaved = new EventEmitter<string>();
  @Output() closeEditor = new EventEmitter<void>();
  @Output() uploadStatus = new EventEmitter<PictureUploadState>();
  unitForm: FormGroup = new FormGroup({});

  undoEnabled: boolean;
  redoEnabled: boolean;

  moveForwardFloorDisabled: boolean;
  moveBackFloorDisabled: boolean;

  moveBackUnitsDisabled: boolean;

  saveDisabled: boolean;

  selectedUnitType: LayoutUnitTypeEnum;

  layout: Layout;

  decrementUnitsDisabled: boolean;
  decrementFloorsDisabled: boolean;

  businessUnit: LayoutUnitTypeEnum = LayoutUnitTypeEnum.businessUnit;
  livingUnit: LayoutUnitTypeEnum = LayoutUnitTypeEnum.livingUnit;
  serviceUnit: LayoutUnitTypeEnum = LayoutUnitTypeEnum.serviceUnit;
  minusOne: LayoutUnitTypeEnum = LayoutUnitTypeEnum.minusOne;
  empty: LayoutUnitTypeEnum = LayoutUnitTypeEnum.empty;

  @ViewChild(InsideSurveyBuildingLayoutCanvasComponent)
  editorCanvas: InsideSurveyBuildingLayoutCanvasComponent;

  shown: boolean = true;

  constructor(
    readonly service: InsideSurveyService,
    readonly buildingLayoutService: BuildingLayoutService,
    readonly fb: FormBuilder,
    readonly modalService: NgbModal,
    private readonly dataService: DataConvertingService,
    private _savingStateService: SavingStateService,
    private readonly toastService: ToastService
  ) {}

  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  ngOnInit() {
    this.unitForm = this.fb.group({
      floors: ["", []],
      units: ["", []],
    });

    this.buildingLayoutService.loadLayout(this.survey.buildingLayout);

    this.buildingLayoutService
      .getLayout()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.layout = res;
        this.saveDisabled = !res.isValid;

        this.selectedUnitType = _.get(
          this.buildingLayoutService.getSelectedUnit(),
          "type"
        );
        if (this.buildingLayoutService.draggingUnitType !== undefined)
          this.selectedUnitType = this.buildingLayoutService.draggingUnitType;

        const floors = res.floors.length;
        const units = this.buildingLayoutService.getSelectedFloorUnitsCount();
        this.unitForm.patchValue({
          floors,
          units,
        });

        this.decrementUnitsDisabled = units <= 1;
        this.decrementFloorsDisabled = floors <= 1;
      });

    this.buildingLayoutService
      .getSelectedUnitPosition()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.selectedUnitType = _.get(
          this.buildingLayoutService.getSelectedUnit(),
          "type"
        );
        if (this.buildingLayoutService.draggingUnitType !== undefined)
          this.selectedUnitType = this.buildingLayoutService.draggingUnitType;

        const floors = this.layout.floors.length;
        const units = this.buildingLayoutService.getSelectedFloorUnitsCount();
        this.unitForm.patchValue({
          floors,
          units,
        });

        this.decrementUnitsDisabled = units <= 1;
        this.decrementFloorsDisabled = floors <= 1;
      });

    this.buildingLayoutService
      .canUndoChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.undoEnabled = res;
      });

    this.buildingLayoutService
      .canRedoChange()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.redoEnabled = res;
      });

    this.buildingLayoutService.draggingUnitTypeSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.selectedUnitType = _.get(
          this.buildingLayoutService.getSelectedUnit(),
          "type"
        );
        if (res !== undefined) this.selectedUnitType = res;
      });
    this.unitForm
      .get("floors")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        let initial = this.buildingLayoutService.getFloorCount();
        if (initial == val) return;
        if (initial > val) this.decrementFloors();
        else this.incrementFloors();
      });
    this.unitForm
      .get("units")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((val) => {
        let initial = this.buildingLayoutService.getSelectedFloorUnitsCount();
        if (initial == val) return;
        if (initial > val) this.decrementUnits();
        else this.incrementUnits();
      });
  }

  isUnitTypeSelected(unitType: LayoutUnitTypeEnum) {
    return this.selectedUnitType === unitType;
  }

  unitTypeSelectorClicked(unitType: LayoutUnitTypeEnum) {
    this.buildingLayoutService.updateSelectedUnit({ type: unitType });
  }

  unitTypeSelectorDragStart(unitType: LayoutUnitTypeEnum) {
    this.buildingLayoutService.draggingUnitType = unitType;
  }

  unitTypeSelectorDragEnd() {
    this.buildingLayoutService.dropDraggingUnitType();
  }

  incrementUnits() {
    this.buildingLayoutService.incrementUnits();
  }

  decrementUnits() {
    this.buildingLayoutService.decrementUnits();
  }

  incrementFloors() {
    this.buildingLayoutService.incrementFloors();
  }

  decrementFloors() {
    this.buildingLayoutService.decrementFloors();
  }

  backButtonClicked() {
    const state = this.buildingLayoutService.getState();
    const layoutState = state.layout;
    const layoutToSave = Layout.create(layoutState);
    if (!layoutToSave.sameTo(this.survey.buildingLayout)) {
      const modalRef = this.modalService.open(UnsavedDialogComponent);
      var yes = modalRef.componentInstance.yes.subscribe(() => {
        yes.unsubscribe();
        no.unsubscribe();
        this.save();
      });
      var no = modalRef.componentInstance.no.subscribe(() => {
        yes.unsubscribe();
        no.unsubscribe();
        this.buildingLayoutService.loadLayout(this.survey.buildingLayout);
        this.closeEditor.emit();
      });
    } else {
      this.closeEditor.emit();
    }
  }

  save() {
    const state = this.buildingLayoutService.getState();
    const layoutState = state.layout;
    const layoutToSave = Layout.create(layoutState);
    this.uploadStatus.emit(PictureUploadState.ongoing);
    const imageBase64 = this.editorCanvas.getImage();
    this.imageSaved.emit(imageBase64);
    const imageData = this.dataService.parseEmbeddedUrlFromBase64(imageBase64);
    var dto = new LayoutDto(
      this.survey.id,
      imageData.data,
      imageData.mimetype,
      layoutToSave
    );
    if (navigator.onLine) {
      this.service
        .updateLayout(dto)
        .pipe(first())
        .subscribe(
          () => {
            this.uploadStatus.emit(PictureUploadState.done);
            this.survey.updateLayout(layoutToSave);
            this.buildingLayoutService.loadLayout(layoutToSave);
          },
          (err) => {
            this.uploadStatus.emit(PictureUploadState.failed);
            this.survey.updateLayout(layoutToSave);
            this._savingStateService.addInsideSurveyCablingLayout(dto);
            this.buildingLayoutService.loadLayout(layoutToSave);
          }
        );
    } else {
      this.uploadStatus.emit(PictureUploadState.failed);
      this.survey.updateLayout(layoutToSave);
      this._savingStateService.addInsideSurveyCablingLayout(dto);
      this.buildingLayoutService.loadLayout(layoutToSave);
    }

    this.closeEditor.emit();
    this.shown = false; // Workaround te redraw canvas
    setTimeout(() => (this.shown = true), 0);
  }

  undo() {
    this.buildingLayoutService.undoChange();
  }

  redo() {
    this.buildingLayoutService.redoChange();
  }
}
